/**
 * Governance → Desktop mascot gallery → Add several outfits (components/BulkOutfitsSection.tsx), without React or the
 * network (unit-tested in apps/admin/test/bulk-outfits.test.ts). Owner, 2026-10-09: "build an interface into the admin
 * UI that allows me to add categories and upload outfit images" and "it will need to allow batch upload": a whole folder
 * of outfits into one category in one go. This file holds:
 *  - which chosen or dropped files join the list (PNG and WebP; a JPEG cannot have a transparent background), a dropped
 *    folder's files, what a file is by its first bytes, and the name an outfit gets from its file name;
 *  - whether a file goes up as it is, is prepared for desktops first (too large, too big a side, not a 32-bit PNG, a
 *    WebP, or mirrored), or cannot be added;
 *  - each row's status against the gallery: ready, to be prepared, already in the gallery by name (any letter case) or
 *    by its exact image, a name the service would refuse, or no room (the catalog's MASCOT_MAX_OUTFITS at once, and every
 *    outfit Gyro keeps), so the list says before anything is sent how many fit and which rows will not be uploaded;
 *  - a new category typed next to the Category select (saved into the registry before the upload);
 *  - what the upload does after each refusal (send again with the revision read again, skip the row, or stop), and the
 *    wording of the count, the room and the result.
 * The service checks every image again, as it does for Add an outfit.
 */
import {
  MASCOT_LIMITS,
  galleryOutfits,
  keptDuplicateOf,
  mascotErrorCode,
  mascotLimitsOf,
  nameRefusal,
  type AdminOutfit,
  type DesktopMascotCatalog,
  type MascotPngCheck,
  type MascotRefusalReason,
} from './desktopMascot';
import {
  CATEGORIES_MAX,
  categoriesRefusal,
  categoriesSaveBody,
  categoryNameRefusal,
  freshCategoryId,
  type CategoriesView,
  type OutfitCategory,
  type SaveCategoriesBody,
} from './outfitCategories';

/** The most files the list holds at once: more than the catalog's 100 outfits, so a big folder can be trimmed down here. */
export const BULK_MAX_FILES = 200;
/** A dropped folder: how deep its subfolders are read, and how many entries are looked at in all (a huge folder never hangs the page). */
export const BULK_FOLDER_DEPTH = 3;
export const BULK_MAX_ENTRIES = 2000;
/** An add refused because another admin changed the gallery is sent again this many times, each with the revision read again. */
export const BULK_CHANGED_RETRIES = 2;
/** The longer side of each row's picture, in pixels (shown at half that, sharp on a high-density screen). */
export const BULK_THUMB_PX = 96;
/** Under the Mirror toggles (owner, 2026-10-09: team outfits carry logos and lettering). */
export const MIRROR_WARNING = 'Mirroring also reverses any lettering and logos; leave it off for outfits with team logos or words.';

const plural = (count: number, one: string, many: string = `${one}s`): string => `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;
const quoted = (name: string): string => `“${name}”`;

// ---- Which files join the list ----

/** A chosen or dropped file, as far as the list needs it (a File in the browser). */
export interface FileLike {
  name: string;
  type: string;
  size: number;
  lastModified: number;
}

const CANDIDATE_TYPES = new Set(['image/png', 'image/webp']);
const CANDIDATE_NAME = /\.(png|webp)$/i;
/** Files an operating system leaves in folders: never listed, never counted. */
const SYSTEM_FILES = new Set(['thumbs.db', 'desktop.ini', 'ehthumbs.db']);

/**
 * Whether a file joins the list: a PNG or a WebP by its type or its name (its first bytes decide later, sniffImageFormat),
 * else it is left out and said; a hidden or system file (".DS_Store", "._Chiefs.png", "Thumbs.db") is skipped silently.
 */
export function fileKind(file: Pick<FileLike, 'name' | 'type'>): 'candidate' | 'hidden' | 'other' {
  if (file.name.startsWith('.') || SYSTEM_FILES.has(file.name.toLowerCase())) return 'hidden';
  return CANDIDATE_TYPES.has(file.type.toLowerCase()) || CANDIDATE_NAME.test(file.name) ? 'candidate' : 'other';
}

/** The same file chosen twice: the same name, size and modification time. */
export function sameFile(left: FileLike, right: FileLike): boolean {
  return left.name === right.name && left.size === right.size && left.lastModified === right.lastModified;
}

/** A file left out of the list, and why: not a PNG or WebP, already listed, or the list is full. */
export interface LeftOut {
  name: string;
  reason: 'type' | 'listed' | 'full';
}

/** File names in the order people expect from a folder: "Team 2" before "Team 10", letter case aside. */
export function byFileName(left: Pick<FileLike, 'name'>, right: Pick<FileLike, 'name'>): number {
  return left.name.localeCompare(right.name, 'en', { numeric: true, sensitivity: 'base' });
}

/**
 * The files that join the list, in file-name order, and those left out: anything that is not a PNG or WebP, a file
 * already listed, and whatever is past BULK_MAX_FILES (with `listed`, the files already in the list).
 */
export function intake<F extends FileLike>(files: ReadonlyArray<F>, listed: ReadonlyArray<FileLike>, max: number = BULK_MAX_FILES): { accepted: F[]; leftOut: LeftOut[] } {
  const accepted: F[] = [];
  const leftOut: LeftOut[] = [];
  for (const file of [...files].sort(byFileName)) {
    const kind = fileKind(file);
    if (kind === 'hidden') continue;
    if (kind === 'other') leftOut.push({ name: file.name, reason: 'type' });
    else if (listed.some((known) => sameFile(known, file)) || accepted.some((known) => sameFile(known, file))) leftOut.push({ name: file.name, reason: 'listed' });
    else if (listed.length + accepted.length >= max) leftOut.push({ name: file.name, reason: 'full' });
    else accepted.push(file);
  }
  return { accepted, leftOut };
}

const someNames = (names: ReadonlyArray<string>): string => (names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`);

/** The line under the drop zone about the files left out, or null when none were. */
export function leftOutText(leftOut: ReadonlyArray<LeftOut>): string | null {
  const named = (reason: LeftOut['reason']) => leftOut.filter((file) => file.reason === reason).map((file) => file.name);
  const sentences: string[] = [];
  const type = named('type');
  if (type.length > 0) {
    sentences.push(`Left out ${plural(type.length, 'file')} that ${type.length === 1 ? 'is not a PNG or WebP image' : 'are not PNG or WebP images'} (${someNames(type)}): `
      + 'an outfit needs a transparent background, which a JPEG photo cannot have.');
  }
  const listed = named('listed');
  if (listed.length > 0) sentences.push(`${plural(listed.length, 'file')} ${listed.length === 1 ? 'was' : 'were'} already in the list (${someNames(listed)}).`);
  const full = named('full');
  if (full.length > 0) {
    sentences.push(`The list holds ${BULK_MAX_FILES} files at once, so ${plural(full.length, 'file')} ${full.length === 1 ? 'was' : 'were'} left out (${someNames(full)}).`);
  }
  return sentences.length > 0 ? sentences.join(' ') : null;
}

/**
 * A dropped file or folder (the browser's FileSystemEntry, from DataTransferItem.webkitGetAsEntry): a file hands over
 * its File, a folder lists its entries, a batch at a time (an empty batch: no more).
 */
export interface DroppedEntry<F> {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  file?: (success: (file: F) => void, failure?: (error: unknown) => void) => void;
  createReader?: () => { readEntries: (success: (entries: DroppedEntry<F>[]) => void, failure?: (error: unknown) => void) => void };
}

/**
 * Every file in what was dropped: the files themselves and, for a dropped folder, the files in it and in its subfolders
 * down to BULK_FOLDER_DEPTH, looking at BULK_MAX_ENTRIES entries at most. An entry that cannot be read is skipped.
 */
export async function filesFromEntries<F>(entries: ReadonlyArray<DroppedEntry<F>>, options: { depth?: number; maxEntries?: number } = {}): Promise<F[]> {
  const maxDepth = options.depth ?? BULK_FOLDER_DEPTH;
  const maxEntries = options.maxEntries ?? BULK_MAX_ENTRIES;
  const files: F[] = [];
  const queue: Array<{ entry: DroppedEntry<F>; depth: number }> = entries.map((entry) => ({ entry, depth: 0 }));
  let looked = 0;
  const fileOf = (entry: DroppedEntry<F>) => new Promise<F>((resolve, reject) => entry.file!(resolve, reject));
  const childrenOf = (entry: DroppedEntry<F>) => new Promise<DroppedEntry<F>[]>((resolve, reject) => {
    const reader = entry.createReader!();
    const children: DroppedEntry<F>[] = [];
    const next = () => reader.readEntries((batch) => {
      children.push(...batch);
      if (batch.length === 0 || children.length >= maxEntries) resolve(children);
      else next();
    }, reject);
    next();
  });
  while (queue.length > 0 && looked < maxEntries) {
    const { entry, depth } = queue.shift()!;
    looked += 1;
    try {
      if (entry.isFile && entry.file) files.push(await fileOf(entry));
      else if (entry.isDirectory && entry.createReader && depth < maxDepth) {
        for (const child of await childrenOf(entry)) queue.push({ entry: child, depth: depth + 1 });
      }
    } catch {
      // An entry the browser cannot read (removed meanwhile, or no permission): the rest still come.
    }
  }
  return files;
}

// ---- What a file is, and its name ----

export type ImageFormat = 'png' | 'webp' | 'jpeg' | 'gif' | 'unknown';

const startsWith = (bytes: Uint8Array, offset: number, values: ReadonlyArray<number>): boolean =>
  bytes.length >= offset + values.length && values.every((value, index) => bytes[offset + index] === value);
const ascii = (text: string): number[] => [...text].map((character) => character.charCodeAt(0));

/** What a file is by its first bytes, whatever its name says. */
export function sniffImageFormat(bytes: Uint8Array): ImageFormat {
  if (startsWith(bytes, 0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) return 'png';
  if (startsWith(bytes, 0, ascii('RIFF')) && startsWith(bytes, 8, ascii('WEBP'))) return 'webp';
  if (startsWith(bytes, 0, [0xff, 0xd8, 0xff])) return 'jpeg';
  if (startsWith(bytes, 0, ascii('GIF8'))) return 'gif';
  return 'unknown';
}

/** Whether a WebP is animated: its extended header (VP8X) has the animation flag. */
export function isAnimatedWebp(bytes: Uint8Array): boolean {
  return startsWith(bytes, 12, ascii('VP8X')) && bytes.length > 20 && (bytes[20]! & 0x02) !== 0;
}

/**
 * The name an outfit gets from its file name: the folder and the extension taken off, hyphens and underscores as
 * spaces, spaces closed up, cut to the name limit (at the last space when the cut would split a word and that keeps at
 * least half the name). "Kansas-City-Chiefs.png" is "Kansas City Chiefs".
 */
export function nameFromFileName(fileName: string, max: number = MASCOT_LIMITS.nameMax): string {
  const base = fileName.replace(/^.*[\\/]/, '');
  const stem = base.includes('.') ? base.slice(0, base.lastIndexOf('.')) : base;
  const words = stem.replace(/[-_]+/g, ' ').replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  if (words.length <= max) return words;
  const cut = words.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return (words[max] !== ' ' && space >= max / 2 ? cut.slice(0, space) : cut).trim();
}

/** A name as two outfits are compared by: spaces closed up and trimmed, any letter case. */
export function normalizeName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLowerCase();
}

// ---- Whether a file is prepared for desktops ----

export const JPEG_REFUSAL = 'This file is a JPEG photo, which cannot have a transparent background, so desktops would show a solid box. Export the outfit as a PNG with a transparent background.';
export const NOT_IMAGE_REFUSAL = 'This file is not a PNG or WebP image. Export the outfit as a PNG with a transparent background.';
export const ANIMATED_REFUSAL = 'The image is animated. Desktops show a still image; export a single frame as a PNG.';
export const PREPARE_FAILED = 'This browser could not prepare the image for desktops. Export it within the limits under Add an outfit and add it again.';
export const DECODE_FAILED = 'This browser could not read the image. Export it again.';
export const UNREADABLE_FILE = 'This file could not be read. Choose it again.';

/** PNG refusals a canvas fixes by drawing the image again as an 8-bit RGBA PNG. */
const CONVERTIBLE: ReadonlySet<MascotRefusalReason> = new Set(['not_rgba8', 'interlaced']);

export type Preparation = { kind: 'as-is' } | { kind: 'prepare' } | { kind: 'refused'; message: string };

/**
 * What happens to a file before it can go up: as it is; prepared for desktops (mirrored when asked, else because it is
 * over the byte or side limit, a PNG that is not 8-bit RGBA or is interlaced, or a WebP), as "Prepare for desktops" does
 * under Add an outfit; or refused (a JPEG, an animation, anything else that is not a PNG or WebP, or a PNG broken in a way
 * drawing it again would not fix: too small, too narrow, damaged). The prepared result is checked again like any file.
 */
export function bulkPreparation(format: ImageFormat, check: MascotPngCheck | null, mirror: boolean, animated = false): Preparation {
  if (format === 'webp') return animated ? { kind: 'refused', message: ANIMATED_REFUSAL } : { kind: 'prepare' };
  if (format === 'jpeg') return { kind: 'refused', message: JPEG_REFUSAL };
  if (format !== 'png' || !check) return { kind: 'refused', message: NOT_IMAGE_REFUSAL };
  if (!check.header) return { kind: 'refused', message: check.refusal?.message ?? NOT_IMAGE_REFUSAL };
  // An animated PNG is refused whatever else is wrong with it: drawing it again would keep its first frame unasked.
  if (check.header.chunkTypes.includes('acTL')) return { kind: 'refused', message: ANIMATED_REFUSAL };
  if (mirror || check.overLimits || (check.refusal !== null && CONVERTIBLE.has(check.refusal.reason))) return { kind: 'prepare' };
  return check.refusal ? { kind: 'refused', message: check.refusal.message } : { kind: 'as-is' };
}

/**
 * The line under a prepared row: what was converted and what "Prepare for desktops" did, from its own line
 * ("Prepared: trimmed, resized from …" becomes "Converted from WebP to PNG; trimmed, resized from …").
 */
export function preparationNote(format: ImageFormat, check: MascotPngCheck | null, preparedLine: string): string {
  const steps = preparedLine.replace(/^Prepared: /, '');
  const reason = check?.refusal?.reason;
  const converted = format === 'webp' ? 'converted from WebP to PNG; '
    : reason === 'not_rgba8' ? 'converted to a 32-bit PNG; '
      : reason === 'interlaced' ? 'converted to a PNG without interlacing; ' : '';
  const text = `${converted}${steps}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * A file once checked (mascotCanvas.ts analyzeOutfitFile): what would go up and how it would show, or why it cannot.
 * The list owns `thumbUrl` and revokes it when the row goes or its picture is replaced.
 */
export interface BulkFileAnalysis {
  /** What would be uploaded: the file as chosen, or what "Prepare for desktops" made of it; null when it cannot be added. */
  upload: Blob | null;
  /** The uploaded image's size (the file's own when it cannot be added; 0 when unreadable) and bytes. */
  width: number;
  height: number;
  sizeBytes: number;
  /** sha256 of `upload`, lowercase hex; null when it cannot be added. */
  sha256: string | null;
  /** What preparing it did (preparationNote), or null when it goes as it is. */
  prepared: string | null;
  refusal: string | null;
  warnings: string[];
  /** An object URL of a small PNG: the outfit trimmed and mirrored as desktops will show it; null when it cannot be drawn. */
  thumbUrl: string | null;
}

/** The analysis of a file that could not be read at all. */
export function unreadableAnalysis(sizeBytes = 0): BulkFileAnalysis {
  return { upload: null, width: 0, height: 0, sizeBytes, sha256: null, prepared: null, refusal: UNREADABLE_FILE, warnings: [], thumbUrl: null };
}

// ---- Each row's status ----

/** What an upload made of a row: added (with a note when its audit record is queued, say), or not added and why. */
export type BulkResult = { kind: 'added'; outfitId: string | null; note: string | null } | { kind: 'failed'; text: string };

/** A row as the statuses need it. */
export interface BulkRowState {
  key: string;
  name: string;
  /** The checked file; null while it is (again) being checked. */
  analysis: Pick<BulkFileAnalysis, 'refusal' | 'sha256' | 'prepared'> | null;
  /** Add it although an outfit with its name is in the gallery or higher in the list. */
  addAnyway: boolean;
  result: BulkResult | null;
}

export type BulkStatus =
  | { kind: 'checking' }
  | { kind: 'ready' }
  | { kind: 'prepared'; detail: string }
  | { kind: 'same-name'; inGallery: boolean; detail: string }
  | { kind: 'name'; detail: string }
  | { kind: 'refused'; detail: string }
  | { kind: 'no-room'; detail: string }
  | { kind: 'uploading' }
  | { kind: 'added'; detail: string | null }
  | { kind: 'failed'; detail: string };

/** What the gallery tells the statuses: its outfits, its limits, and the outfits it keeps outside the catalog. */
export type BulkCatalog = Pick<DesktopMascotCatalog, 'outfits' | 'limits'> & Partial<Pick<DesktopMascotCatalog, 'days' | 'retired' | 'orphans' | 'dayWarnings'>>;

/**
 * How many more outfits fit now, and which limit leaves the least room: the catalog at once, or every outfit kept. On a
 * tie the total decides: retiring an outfit makes no room under it, so the list never tells the admin to retire outfits
 * when that would not help (a service that holds as many at once as it keeps, with none retired, ties at every count).
 */
export function roomOf(catalog: Pick<DesktopMascotCatalog, 'outfits' | 'limits'>): { room: number; limit: 'catalog' | 'total' } {
  const limits = mascotLimitsOf(catalog);
  const inCatalog = Math.max(0, limits.catalog - catalog.outfits.length);
  const inTotal = limits.used === null ? Number.POSITIVE_INFINITY : Math.max(0, limits.total - limits.used);
  return inTotal <= inCatalog ? { room: inTotal, limit: 'total' } : { room: inCatalog, limit: 'catalog' };
}

const NO_ROOM_DETAIL = 'The gallery is full once the outfits above it are added, so this one is not uploaded.';

/**
 * Each row's status, top to bottom: added or not added by an upload; being added; being checked; a file that cannot be
 * added; its exact image already kept (in the catalog, retired, or never finished) or higher in the list; a name the
 * service would refuse; a name already in the gallery or higher in the list, in any letter case (skipped unless "Add
 * anyway"); else ready or to be prepared, while there is room: the rows past the room are "No room". A row being added
 * takes its room and its name first.
 */
export function bulkStatuses(rows: ReadonlyArray<BulkRowState>, catalog: BulkCatalog, uploadingKey: string | null = null): BulkStatus[] {
  const galleryNames = new Map(galleryOutfits(catalog).map((outfit) => [normalizeName(outfit.name), outfit.name] as const));
  const stored = new Map(catalog.outfits.map((outfit) => [outfit.sha256, outfit] as const));
  const seenImages = new Map<string, string>();
  const seenNames = new Map<string, string>();
  let { room } = roomOf(catalog);
  return rows.map((row): BulkStatus => {
    if (row.result?.kind === 'added') return { kind: 'added', detail: row.result.note };
    if (row.result?.kind === 'failed') return { kind: 'failed', detail: row.result.text };
    const name = row.name.trim();
    if (row.key === uploadingKey) {
      if (row.analysis?.sha256) seenImages.set(row.analysis.sha256, name);
      seenNames.set(normalizeName(name), name);
      room = Math.max(0, room - 1);
      return { kind: 'uploading' };
    }
    if (!row.analysis) return { kind: 'checking' };
    if (row.analysis.refusal) return { kind: 'refused', detail: row.analysis.refusal };
    const sha256 = row.analysis.sha256;
    if (sha256) {
      const same = stored.get(sha256);
      if (same) return { kind: 'refused', detail: `This exact image is already in the catalog as ${quoted(same.name)}, so it is not added again.` };
      const kept = keptDuplicateOf(sha256, catalog);
      if (kept) {
        return {
          kind: 'refused',
          detail: kept.retired
            ? `This exact image is the retired outfit ${quoted(kept.outfit.name)}: restore it under Retired, in Day outfits below, instead of adding it again.`
            : `This exact image is ${quoted(kept.outfit.name)}, which never reached the gallery: restore it under Needs attention, in Day outfits below.`,
        };
      }
      const earlier = seenImages.get(sha256);
      if (earlier !== undefined) return { kind: 'refused', detail: `This is the same image as ${quoted(earlier)} above, which is added once.` };
      seenImages.set(sha256, name);
    }
    const named = nameRefusal(row.name);
    if (named) return { kind: 'name', detail: named };
    const key = normalizeName(name);
    if (!row.addAnyway) {
      const inGallery = galleryNames.get(key);
      if (inGallery !== undefined) {
        return { kind: 'same-name', inGallery: true, detail: `An outfit named ${quoted(inGallery)} is already in the gallery, so this one is skipped. Rename it, or choose Add anyway.` };
      }
      const above = seenNames.get(key);
      if (above !== undefined) {
        return { kind: 'same-name', inGallery: false, detail: `${quoted(above)} above has the same name, so this one is skipped. Rename it, or choose Add anyway.` };
      }
    }
    seenNames.set(key, name);
    if (room <= 0) return { kind: 'no-room', detail: NO_ROOM_DETAIL };
    room -= 1;
    return row.analysis.prepared ? { kind: 'prepared', detail: row.analysis.prepared } : { kind: 'ready' };
  });
}

/** Whether the next upload sends this row. */
export function isUploadable(status: BulkStatus): boolean {
  return status.kind === 'ready' || status.kind === 'prepared';
}

/** A status in a few words, as its badge says it. */
export function statusLabel(status: BulkStatus): string {
  switch (status.kind) {
    case 'checking': return 'Checking…';
    case 'ready': return 'Ready';
    case 'prepared': return 'Will be prepared for desktops';
    case 'same-name': return status.inGallery ? 'Already in the gallery' : 'Same name as another file';
    case 'name': return 'Fix the name';
    case 'refused': return 'Cannot be added';
    case 'no-room': return 'No room';
    case 'uploading': return 'Adding…';
    case 'added': return 'Added';
    case 'failed': return 'Not added';
  }
}

/**
 * The count over the list: "32 ready, 0 skipped"; with what an upload did first ("30 added, 2 not added"), the files
 * still being checked, and how many will be prepared for desktops.
 */
export function countLine(statuses: ReadonlyArray<BulkStatus>): string {
  const of = (...kinds: BulkStatus['kind'][]) => statuses.filter((status) => kinds.includes(status.kind)).length;
  const ready = of('ready', 'prepared');
  const skipped = of('same-name', 'name', 'refused', 'no-room');
  const added = of('added');
  const failed = of('failed');
  const uploaded = added + failed + of('uploading') > 0;
  const parts: string[] = [];
  if (added > 0) parts.push(`${added.toLocaleString('en-US')} added`);
  if (failed > 0) parts.push(`${failed.toLocaleString('en-US')} not added`);
  if (ready > 0 || !uploaded) parts.push(`${ready.toLocaleString('en-US')} ready`);
  if (skipped > 0 || !uploaded) parts.push(`${skipped.toLocaleString('en-US')} skipped`);
  const checking = of('checking');
  if (checking > 0) parts.push(`${checking.toLocaleString('en-US')} still being checked`);
  const prepared = of('prepared');
  return `${parts.join(', ')}${prepared > 0 ? ` · ${prepared.toLocaleString('en-US')} will be prepared for desktops` : ''}`;
}

/**
 * How many of the ready rows fit, said before anything is sent: all of them, only some (the catalog holds
 * MASCOT_MAX_OUTFITS at once, or Gyro keeps MASCOT_MAX_TOTAL_OUTFITS in all), or none. Null while no row is ready. A
 * gallery with no room at all is said once, over the pickers (fullText): this line then says only what that means for
 * the list.
 */
export function roomText(catalog: Pick<DesktopMascotCatalog, 'outfits' | 'limits'>, statuses: ReadonlyArray<BulkStatus>): string | null {
  const fits = statuses.filter(isUploadable).length;
  const blocked = statuses.filter((status) => status.kind === 'no-room').length;
  if (fits + blocked === 0) return null;
  const { room, limit } = roomOf(catalog);
  if (blocked === 0) return `${fits === 1 ? 'It fits' : `All ${fits.toLocaleString('en-US')} fit`}: the gallery has room for ${plural(room, 'more outfit', 'more outfits')}.`;
  const rows = blocked === 1 ? 'the row marked “No room”' : `the ${blocked.toLocaleString('en-US')} rows marked “No room”`;
  if (fits === 0) return `Nothing more fits, so ${rows} will not be uploaded.`;
  const limits = mascotLimitsOf(catalog);
  const why = limit === 'total'
    ? `Gyro keeps at most ${limits.total.toLocaleString('en-US')} outfits, counting retired ones, and keeps ${(limits.used ?? 0).toLocaleString('en-US')} now`
    : `the catalog holds ${limits.catalog.toLocaleString('en-US')} outfits at once and has ${catalog.outfits.length.toLocaleString('en-US')} now`;
  const fix = limit === 'total' ? 'retired outfits are kept, so retiring one does not make room' : `retire outfits to make room for ${blocked === 1 ? 'it' : 'them'}`;
  const only = `Only ${fits.toLocaleString('en-US')} of these ${(fits + blocked).toLocaleString('en-US')} ${fits === 1 ? 'fits' : 'fit'}`;
  return `${only}: ${why}. ${rows.charAt(0).toUpperCase()}${rows.slice(1)} will not be uploaded; ${fix}.`;
}

// ---- A new category from the Category select ----

export type NewCategory =
  | { kind: 'existing'; id: string; name: string }
  | { kind: 'refused'; message: string }
  | { kind: 'ready'; id: string; name: string; body: SaveCategoriesBody };

/**
 * "New category…": the registry with the typed name added (shown all year; a season is set under Categories), saved
 * whole with the revision it was read at; a name already there in any letter case is that category, not a second one.
 */
export function newCategory(view: Pick<CategoriesView, 'revision' | 'categories'>, typed: string): NewCategory {
  const name = typed.trim();
  const named = categoryNameRefusal(name);
  if (named) return { kind: 'refused', message: named };
  const existing = view.categories.find((category) => category.name.trim().toLowerCase() === name.toLowerCase());
  if (existing) return { kind: 'existing', id: existing.id, name: existing.name };
  if (view.categories.length >= CATEGORIES_MAX) return { kind: 'refused', message: `Gyro keeps at most ${CATEGORIES_MAX} categories. Remove one under Categories below first.` };
  const id = freshCategoryId(name, view.categories);
  if (!id) return { kind: 'refused', message: 'Give the category a name with at least one letter or digit.' };
  const categories: OutfitCategory[] = [
    ...view.categories.map((category) => ({ id: category.id, name: category.name, enabled: category.enabled, window: category.window ? { from: category.window.from, to: category.window.to } : null })),
    { id, name, enabled: true, window: null },
  ];
  const refusal = categoriesRefusal(categories);
  if (refusal) return { kind: 'refused', message: refusal };
  return { kind: 'ready', id, name, body: categoriesSaveBody(categories, view.revision) };
}

// ---- The upload ----

/**
 * What the upload does after a refused add: send it again with the revision read again (another admin changed the
 * gallery: mascot_changed, at most BULK_CHANGED_RETRIES times); count it as added (saved with its audit record queued);
 * read the gallery to see whether it was added (no answer, or a gateway's 5xx without the service's code); skip the row
 * and go on (this image or this name is refused: a duplicate, a rejected or too large image); or stop, because the next
 * add would be refused too (the session, the gallery full, storage or the audit ledger unavailable, the category or the
 * window refused, too many requests).
 */
export type UploadErrorAction = 'retry' | 'saved' | 'check' | 'skip' | 'stop';

export function uploadErrorAction(error: unknown, attempt: number): UploadErrorAction {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  const code = mascotErrorCode(error);
  if (code === 'mascot_changed') return attempt < BULK_CHANGED_RETRIES ? 'retry' : 'stop';
  if (code === 'mascot_audit_pending' || code === 'mascot_audit_failed') return 'saved';
  if (code === 'mascot_duplicate' || code === 'mascot_rejected' || code === 'mascot_too_large') return 'skip';
  if (code === 'invalid_request') {
    const field = (error as { body?: Record<string, unknown> }).body?.field;
    return field === 'category' || field === 'window' ? 'stop' : 'skip';
  }
  if (code !== null) return 'stop';
  if (typeof status !== 'number' || status >= 500) return 'check';
  return status === 400 || status === 413 || status === 422 ? 'skip' : 'stop';
}

/** The outfit an add whose answer never came made, if the gallery read again has it: new since the add, by its image or its name. */
export function addedSince(catalog: Pick<DesktopMascotCatalog, 'outfits'>, before: ReadonlySet<string>, sha256: string | null, name: string): AdminOutfit | null {
  const wanted = normalizeName(name);
  return catalog.outfits.find((outfit) => !before.has(outfit.id) && ((sha256 !== null && outfit.sha256 === sha256) || normalizeName(outfit.name) === wanted)) ?? null;
}

/** The row's note when the add's answer was lost but the outfit is in the gallery. */
export const LOST_ANSWER_TEXT = 'Gyro’s answer did not arrive, but the outfit is in the gallery.';
/** Why the upload stopped when an add got no answer and the outfit is not in the gallery (the connection, most likely). */
export const NO_ANSWER_TEXT = 'Gyro did not answer, and the outfit is not in the gallery. Check the connection.';

export interface UploadOutcome {
  added: number;
  failed: number;
  /** The rows the upload set out to add. */
  planned: number;
  /** Stopped before the end: by the Stop button, or by a refusal the next add would get too (with its sentence). */
  stopped: 'user' | 'error' | null;
  reason?: string;
  /** Where they went: the category's name, or General. */
  category: string;
  /** Added with their audit record queued. */
  queued: number;
}

/** The note after an upload. */
export function uploadSummaryText(outcome: UploadOutcome): { ok: boolean; text: string } {
  const n = (value: number) => value.toLocaleString('en-US');
  const { added, failed, planned } = outcome;
  const left = Math.max(0, planned - added - failed);
  const notAdded = failed > 0 ? ` ${n(failed)} ${failed === 1 ? 'was' : 'were'} not added (each row says why).` : '';
  const rest = (then: string) => (left > 0 ? ` The other ${n(left)} ${left === 1 ? 'is' : 'are'} still in the list; ${then}` : '');
  const queued = outcome.queued > 0
    ? ` ${outcome.queued === 1 ? 'One of them has its' : `${n(outcome.queued)} of them have their`} audit record queued, because the audit ledger refused it just now; it is written with the next change.`
    : '';
  if (outcome.stopped === 'error') {
    return { ok: false, text: `Stopped after adding ${n(added)} of ${n(planned)}: ${outcome.reason ?? 'Gyro refused the next one.'}${notAdded}${rest('add them once that is fixed.')}${queued}` };
  }
  if (outcome.stopped === 'user') {
    return { ok: failed === 0, text: `Stopped: ${n(added)} of ${n(planned)} added to ${outcome.category}.${notAdded}${rest('press Add to add them.')}${queued}` };
  }
  if (failed > 0) return { ok: false, text: `Added ${n(added)} of ${n(planned)} outfits to ${outcome.category}.${notAdded}${queued}` };
  return {
    ok: true,
    text: `Added ${plural(added, 'outfit')} to ${outcome.category}. Desktops pick them up on their next check, within about 20 minutes; each one is in the audit trail.${queued}`,
  };
}
